// The continuous chat: a client-side journal of every chat line the panel has shown, across
// every forecast, kept per user in this browser. The run-store still holds one conversation per
// forecast (and the server still persists them per forecast); the journal only changes what the
// panel DISPLAYS, so sending a message keeps going to the current forecast's conversation.
//
// Journal shape: an ordered array of
//   divider  { kind: "divider", id, forecastClientId, forecastTitle, at }
//   message  { kind: "message", id, forecastClientId, forecastTitle, role, text, ts, at, entry }
// A divider opens a section; the messages after it belong to that visit. `forecastClientId` is
// the run's client id (null for the "New question" state), `at` is when the item entered the
// journal (ms), `ts` the line's own timestamp, and `entry` the render fields the panel needs
// beyond role/text. Messages are deduped by (forecastClientId, id).
//
// Pure: no React, no globals. Storage helpers take the Storage object.

/** The most messages the journal keeps; older ones fall off first. */
export const JOURNAL_MAX_MESSAGES = 1000;
/** Messages older than this (by when they entered the journal) are dropped. */
export const JOURNAL_MAX_AGE_DAYS = 90;
export const JOURNAL_MAX_AGE_MS = JOURNAL_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
/** A stored line is cut to this length; the panel clamps long reports anyway. */
export const JOURNAL_MAX_TEXT_CHARS = 6000;
export const JOURNAL_STORAGE_PREFIX = "sayer-chat-journal";
export const JOURNAL_VERSION = 1;
/** The divider title for the state before a question is asked. */
export const NEW_QUESTION_TITLE = "New question";

/** Metadata the renderer reads from a stored line; everything else is dropped. */
const KEPT_METADATA = ["reportLink", "legend"];

export const EMPTY_JOURNAL = Object.freeze([]);

let dividerSeq = 0;
/** A fresh divider id. */
export function newDividerId(now = Date.now()) {
  dividerSeq = (dividerSeq + 1) % 1679616;
  return `d:${now.toString(36)}:${dividerSeq.toString(36)}`;
}

/** localStorage key for one signed-in user's journal. */
export function journalStorageKey(identity) {
  return `${JOURNAL_STORAGE_PREFIX}:v${JOURNAL_VERSION}:${identity || "local"}`;
}

// djb2 over UTF-16 code units, base 36: short, stable, and enough to tell two lines with the same
// timestamp apart.
function hashText(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** A timestamp (ISO string or ms) as ms; 0 when it can't be read. */
export function toMs(ts) {
  if (typeof ts === "number") return Number.isFinite(ts) ? ts : 0;
  const n = Date.parse(ts);
  return Number.isFinite(n) ? n : 0;
}

const scopeKey = (forecastClientId) => forecastClientId ?? "";
const keyOf = (forecastClientId, id) => `${scopeKey(forecastClientId)}|${id}`;

/**
 * The stable id of a chat line, or null for a line that is still changing (a running forecast's
 * status line, live narration). An entry is identified by its timestamp, role, kind and text —
 * all of which survive a reload, because the server stores the client timestamp with the row. A
 * finished forecast line is identified by its run, status and timestamp.
 */
export function messageIdOf(item) {
  if (!item) return null;
  if (item.kind === "entry") {
    return `e:${item.ts ?? ""}:${item.role ?? ""}:${item.entryKind ?? ""}:${hashText(String(item.content ?? ""))}`;
  }
  if (item.kind === "event" && item.terminal && item.runId) {
    return `r:${item.runId}:${item.status ?? ""}:${item.ts ?? ""}`;
  }
  return null;
}

/** True for a line the journal keeps (a message, a finished forecast line, an error). */
export const isRecordable = (item) => messageIdOf(item) != null;

const clip = (s) =>
  s.length > JOURNAL_MAX_TEXT_CHARS ? `${s.slice(0, JOURNAL_MAX_TEXT_CHARS - 1)}…` : s;

function keptMetadata(md) {
  if (!md || typeof md !== "object") return null;
  let out = null;
  for (const k of KEPT_METADATA) {
    if (md[k] != null && md[k] !== false) (out ||= {})[k] = md[k];
  }
  return out;
}

/** The journal message for one panel line, or null when the line isn't recordable. */
export function toJournalMessage(item, { forecastClientId = null, forecastTitle = null, now }) {
  const id = messageIdOf(item);
  if (!id) return null;
  const base = {
    kind: "message",
    id,
    forecastClientId: forecastClientId ?? null,
    forecastTitle: forecastClientId != null ? forecastTitle || null : null,
    text: clip(String(item.content ?? "")),
    ts: item.ts ?? null,
    at: now,
  };
  if (item.kind === "entry") {
    const metadata = keptMetadata(item.metadata);
    return {
      ...base,
      role: item.role || "assistant",
      entry: {
        kind: "entry",
        entryKind: item.entryKind ?? null,
        ...(metadata ? { metadata } : {}),
      },
    };
  }
  return {
    ...base,
    role: item.status === "errored" ? "error" : "assistant",
    entry: {
      kind: "event",
      runId: item.runId,
      status: item.status ?? null,
      ...(item.detail ? { detail: String(item.detail).slice(0, 500) } : {}),
    },
  };
}

/** The panel's render shape for a stored message (what ChatContent draws). */
export function toRenderItem(m) {
  const e = m.entry || {};
  if (e.kind === "event") {
    return {
      kind: "event",
      runId: e.runId,
      status: e.status,
      terminal: true,
      content: m.text,
      ts: m.ts,
      ...(e.detail ? { detail: e.detail } : {}),
    };
  }
  return {
    kind: "entry",
    role: m.role,
    entryKind: e.entryKind ?? null,
    content: m.text,
    ts: m.ts,
    metadata: e.metadata || {},
  };
}

export const hasMessages = (journal) => !!journal && journal.some((m) => m.kind === "message");

function lastDividerIndex(journal) {
  for (let i = journal.length - 1; i >= 0; i--) if (journal[i].kind === "divider") return i;
  return -1;
}

/** Renames every item of one forecast; the same array when nothing changes. */
export function retitleForecast(journal, forecastClientId, title) {
  if (forecastClientId == null || !title) return journal;
  let out = null;
  journal.forEach((m, i) => {
    if (m.forecastClientId === forecastClientId && m.forecastTitle !== title) {
      out ||= journal.slice();
      out[i] = { ...m, forecastTitle: title };
    }
  });
  return out || journal;
}

/**
 * Makes the journal's last section belong to `scope` (the forecast now on screen, or the "New
 * question" state when `scope.forecastClientId` is null). Returns the same array when it already
 * does.
 *
 * - The section being left, if it has no messages, is replaced rather than stacked, so clicking
 *   through several forecasts doesn't leave a column of empty dividers.
 * - A "New question" section that a new forecast was asked from (the forecast was created after
 *   the section opened) becomes that forecast's section, so the question and its answer read as
 *   one.
 * - Nothing is added for the "New question" state while the journal has no messages: with no
 *   history there is nothing to separate it from.
 * - `forceNew` opens a fresh section even for the same scope (a second "New question").
 * - `section` fixes the new divider's id and time, so the same divider computed for display and
 *   then recorded keeps one identity.
 */
export function ensureSection(
  journal,
  scope,
  { now = Date.now(), section = null, forceNew = false } = {},
) {
  const j = journal || EMPTY_JOURNAL;
  const forecastClientId = scope?.forecastClientId ?? null;
  const title = forecastClientId != null ? scope?.forecastTitle || null : null;
  const lastIdx = lastDividerIndex(j);
  const last = lastIdx >= 0 ? j[lastIdx] : null;
  const sameScope = !!last && scopeKey(last.forecastClientId) === scopeKey(forecastClientId);
  if (last && section?.id && last.id === section.id)
    return retitleForecast(j, forecastClientId, title);
  if (sameScope && !forceNew) return retitleForecast(j, forecastClientId, title);
  // Lines before any divider are already "New question" lines (a forecast always opens one).
  if (forecastClientId == null && (!last || !hasMessages(j))) return j;
  const divider = {
    kind: "divider",
    id: section?.id || newDividerId(now),
    forecastClientId,
    forecastTitle: title,
    at: section?.at ?? now,
  };
  if (last && lastIdx === j.length - 1) return [...j.slice(0, -1), divider];
  const createdMs = toMs(scope?.createdAt);
  if (
    !last &&
    forecastClientId != null &&
    j.length > 0 &&
    j.every((m) => m.forecastClientId == null) &&
    createdMs >= j[0].at
  ) {
    // The first question asked from a history-less chat (the tour's welcome, say) heads the
    // lines that led to it.
    return [{ ...divider, at: Math.min(divider.at, j[0].at) }, ...j];
  }
  if (last && last.forecastClientId == null && forecastClientId != null && createdMs >= last.at) {
    const out = j.slice();
    out[lastIdx] = { ...last, forecastClientId, forecastTitle: title };
    return retitleForecast(out, forecastClientId, title);
  }
  return [...j, divider];
}

/**
 * Adds journal messages, deduped by (forecastClientId, id). A line already recorded is updated
 * in place when its text changed (a finished forecast line gains its duration). A line recorded
 * in the "New question" state that turns up in a forecast's conversation (the draft is adopted
 * when the question is asked) is moved to that forecast rather than added twice. Returns the
 * same array when nothing changes.
 */
export function appendMessages(journal, messages) {
  const j = journal || EMPTY_JOURNAL;
  if (!messages || messages.length === 0) return j;
  const index = new Map();
  j.forEach((m, i) => {
    if (m.kind === "message") index.set(keyOf(m.forecastClientId, m.id), i);
  });
  let out = null;
  for (const m of messages) {
    if (!m) continue;
    const k = keyOf(m.forecastClientId, m.id);
    const i = index.get(k);
    if (i !== undefined) {
      const prev = (out || j)[i];
      if (prev.text !== m.text || prev.role !== m.role) {
        out ||= j.slice();
        out[i] = { ...prev, text: m.text, role: m.role, entry: m.entry };
      }
      continue;
    }
    if (m.forecastClientId != null) {
      const draftKey = keyOf(null, m.id);
      const di = index.get(draftKey);
      if (di !== undefined) {
        out ||= j.slice();
        out[di] = {
          ...out[di],
          forecastClientId: m.forecastClientId,
          forecastTitle: m.forecastTitle,
        };
        index.delete(draftKey);
        index.set(k, di);
        continue;
      }
    }
    out ||= j.slice();
    index.set(k, out.length);
    out.push(m);
  }
  return out || j;
}

/**
 * Applies the caps: the newest JOURNAL_MAX_MESSAGES messages, none older than
 * JOURNAL_MAX_AGE_DAYS, whichever leaves fewer. Dividers whose section is left empty go too
 * (except a trailing one: the section being written). Same array when nothing is dropped.
 */
export function pruneJournal(
  journal,
  now = Date.now(),
  { maxMessages = JOURNAL_MAX_MESSAGES, maxAgeMs = JOURNAL_MAX_AGE_MS } = {},
) {
  const j = journal || EMPTY_JOURNAL;
  const cutoff = now - maxAgeMs;
  const kept = [];
  let count = 0;
  for (let i = j.length - 1; i >= 0; i--) {
    const it = j[i];
    if (it.kind === "message") {
      if (count >= maxMessages || !(it.at >= cutoff)) continue;
      count++;
    }
    kept.push(it);
  }
  kept.reverse();
  const out = kept.filter(
    (it, i) => it.kind !== "divider" || i === kept.length - 1 || kept[i + 1].kind !== "divider",
  );
  return out.length === j.length ? j : out;
}

/**
 * Records the panel's current lines: makes sure the last section is the current scope's, then
 * adds every recordable line, then applies the caps. Same array when nothing changes.
 *
 * @param {object[]} journal
 * @param {object[]} liveItems the panel's lines for the current scope (the run-store projection)
 * @param {{forecastClientId: string|null, forecastTitle: string|null, createdAt?: string|number}} scope
 * @param {{now?: number, section?: {id: string, at: number}, forceNew?: boolean}} [opts]
 */
export function recordLive(
  journal,
  liveItems,
  scope,
  { now = Date.now(), section = null, forceNew = false } = {},
) {
  let j = journal || EMPTY_JOURNAL;
  const forecastClientId = scope?.forecastClientId ?? null;
  const messages = [];
  for (const it of liveItems || EMPTY_JOURNAL) {
    const m = toJournalMessage(it, {
      forecastClientId,
      forecastTitle: scope?.forecastTitle,
      now,
    });
    if (m) messages.push(m);
  }
  if (needsSection(j, liveItems, scope)) j = ensureSection(j, scope, { now, section, forceNew });
  j = appendMessages(j, messages);
  return j === journal ? journal : pruneJournal(j, now);
}

/** Whether the current scope gets a divider: always once there is history, and for a forecast
 * with lines to show even when there isn't (the divider names the question). */
export function needsSection(journal, liveItems, scope) {
  if (hasMessages(journal)) return true;
  return scope?.forecastClientId != null && (liveItems?.length ?? 0) > 0;
}

const KIND_ORDER = { entry: 0, event: 1, narration: 2 };
function compareLines(a, b) {
  const ta = toMs(a.ts);
  const tb = toMs(b.ts);
  if (ta !== tb) return ta - tb;
  const ka = KIND_ORDER[a.kind] ?? 1;
  const kb = KIND_ORDER[b.kind] ?? 1;
  if (ka !== kb) return ka - kb;
  if (a.kind === "narration" && b.kind === "narration") return (a.ordinal ?? 0) - (b.ordinal ?? 0);
  return 0;
}

/**
 * The panel's display list: the whole journal with the current scope's live lines merged in.
 *
 * - A recorded line renders with its live copy when the current conversation still has it.
 * - Live lines not yet recorded (just arrived, or still running) join the last section.
 * - Each section is ordered by the lines' own timestamps (a thread can load after its forecast's
 *   result line was recorded).
 * - Dividers come out as `{kind: "divider", title, at, forecastClientId, current, _jkey}`;
 *   recorded lines carry `_jkey` (a stable React key) and `_forecastClientId`/`_foreign` (true
 *   when the line belongs to a forecast other than the current one).
 */
export function buildChatDisplay(
  journal,
  liveItems,
  scope,
  { now = Date.now(), section = null, forceNew = false } = {},
) {
  const live = liveItems || EMPTY_JOURNAL;
  const curId = scope?.forecastClientId ?? null;
  const cur = scopeKey(curId);
  let j = journal || EMPTY_JOURNAL;
  if (needsSection(j, live, scope)) j = ensureSection(j, scope, { now, section, forceNew });

  const liveById = new Map();
  for (const it of live) {
    const id = messageIdOf(it);
    if (id && !liveById.has(id)) liveById.set(id, it);
  }
  const matched = new Set();
  const usedKeys = new Set();
  const uniqueKey = (base) => {
    let k = base;
    for (let n = 2; usedKeys.has(k); n++) k = `${base}#${n}`;
    usedKeys.add(k);
    return k;
  };

  const sections = [{ divider: null, lines: [] }];
  for (const m of j) {
    if (m.kind === "divider") {
      sections.push({ divider: m, lines: [] });
      continue;
    }
    const mine = scopeKey(m.forecastClientId) === cur || m.forecastClientId == null;
    const liveIt = mine && !matched.has(m.id) ? liveById.get(m.id) : undefined;
    if (liveIt) matched.add(m.id);
    const foreign = m.forecastClientId != null && scopeKey(m.forecastClientId) !== cur;
    sections[sections.length - 1].lines.push({
      ...(liveIt || toRenderItem(m)),
      _jkey: uniqueKey(`j:${m.id}`),
      _forecastClientId: m.forecastClientId,
      _foreign: foreign,
    });
  }
  const tail = sections[sections.length - 1];
  for (const it of live) {
    const id = messageIdOf(it);
    if (id && matched.has(id)) continue;
    if (id) matched.add(id);
    tail.lines.push(id ? { ...it, _jkey: uniqueKey(`j:${id}`), _forecastClientId: curId } : it);
  }

  const out = [];
  const lastDividerSection = sections.length - 1;
  sections.forEach((s, si) => {
    if (s.divider) {
      const d = s.divider;
      out.push({
        kind: "divider",
        _jkey: uniqueKey(`j:${d.id}`),
        forecastClientId: d.forecastClientId,
        title:
          d.forecastClientId != null ? d.forecastTitle || "Untitled question" : NEW_QUESTION_TITLE,
        at: d.at,
        current: si === lastDividerSection && scopeKey(d.forecastClientId) === cur,
      });
    }
    out.push(...s.lines.slice().sort(compareLines));
  });
  return out;
}

/** True when nothing follows the display list's last divider (the current section is empty). */
export function currentSectionEmpty(displayItems) {
  for (let i = displayItems.length - 1; i >= 0; i--) {
    if (displayItems[i].kind === "divider") return i === displayItems.length - 1;
  }
  return displayItems.length === 0;
}

/** "2:41 PM" today, "Oct 3, 2:41 PM" this year, "Oct 3, 2025" before that. */
export function formatDividerTime(at, now = Date.now()) {
  const d = new Date(at);
  if (!Number.isFinite(d.getTime())) return "";
  const n = new Date(now);
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (d.toDateString() === n.toDateString()) return time;
  const day = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (d.getFullYear() === n.getFullYear()) return `${day}, ${time}`;
  return `${day}, ${d.getFullYear()}`;
}

/** The journal as stored. */
export function serializeJournal(journal) {
  return JSON.stringify({ v: JOURNAL_VERSION, items: journal || [] });
}

function validItem(it) {
  if (!it || typeof it !== "object" || typeof it.id !== "string" || typeof it.at !== "number")
    return false;
  if (it.kind === "divider") return true;
  return it.kind === "message" && typeof it.text === "string" && !!it.entry;
}

/** A stored journal, or an empty one when it is missing, unreadable or from another version. */
export function parseJournal(raw) {
  if (!raw) return EMPTY_JOURNAL;
  try {
    const d = JSON.parse(raw);
    if (!d || d.v !== JOURNAL_VERSION || !Array.isArray(d.items)) return EMPTY_JOURNAL;
    return d.items.filter(validItem);
  } catch {
    return EMPTY_JOURNAL;
  }
}

/** Reads and prunes a stored journal; empty when storage is unavailable. */
export function loadJournal(storage, key, now = Date.now()) {
  try {
    return pruneJournal(parseJournal(storage?.getItem(key)), now);
  } catch {
    return EMPTY_JOURNAL;
  }
}

/**
 * Writes the journal. When the browser's quota is full it retries with the newest half, then
 * gives up (the journal still works for this page). Returns whether a write landed.
 */
export function saveJournal(storage, key, journal, now = Date.now()) {
  if (!storage) return false;
  let j = journal || EMPTY_JOURNAL;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      if (j.length === 0) storage.removeItem(key);
      else storage.setItem(key, serializeJournal(j));
      return true;
    } catch {
      const msgs = j.filter((m) => m.kind === "message").length;
      if (msgs <= 1) return false;
      j = pruneJournal(j, now, { maxMessages: Math.floor(msgs / 2) });
    }
  }
  return false;
}
